import React, { useState, useEffect } from "react";

// Helper to import all images from a folder (Webpack only)
function importAll(r) {
  return r.keys().map(r);
}

// Dynamically import images for each case study (adjust folder names as needed)
const uniqueStudios = importAll(
  require.context("../Assets/Slide1", false, /\.(png|jpe?g|svg)$/)
);
const crostFlow = importAll(
  require.context("../Assets/Slide2", false, /\.(png|jpe?g|svg)$/)
);
const havilah = importAll(
  require.context("../Assets/Slide3", false, /\.(png|jpe?g|svg)$/)
);
const activeVibes = importAll(
  require.context("../Assets/Slide4", false, /\.(png|jpe?g|svg)$/)
);
const aichy = importAll(
  require.context("../Assets/Slide5", false, /\.(png|jpe?g|svg)$/)
);

// Example data for case studies, now with dynamic images
const caseStudiesData = [
  {
    title: "Brand Identity for Unique Beauty Studio",
    description:
      "Developed a sophisticated and cohesive brand identity for a nail salon, including a modern logo, harmonious color palette, and detailed brand guidelines to elevate their market presence.",
    images: uniqueStudios,
  },
  {
    title: "Brand Identity for Crost Flow",
    description:
      "Designed a distinctive and professional brand identity for a financial services company, featuring a memorable logo, strategic color choices, and comprehensive brand documentation.",
    images: crostFlow,
  },
  {
    title: "Brand Identity for Havilah Fashion",
    description:
      "Created a vibrant and stylish brand identity for a fashion label, delivering a unique logo, bold color scheme, and thorough brand guidelines to reflect their creative vision.",
    images: havilah,
  },
  {
    title: "Brand Identity for Active Vibes",
    description:
      "Developed a dynamic and energetic brand identity for a music studio, including a visually striking logo, lively color palette, and clear brand standards to resonate with their audience.",
    images: activeVibes,
  },
  {
    title: "Brand Identity for Aichy",
    description:
      "Crafted a sleek and modern brand identity for Aichy, focusing on a minimalist logo design, elegant color palette, and comprehensive brand guidelines to enhance their professional image.",
    images: aichy,
  },
];

// Carousel for images inside each case study (with auto-slide)
const ImageCarousel = ({ images, interval = 5000 }) => {
  const [current, setCurrent] = useState(0);

  const prev = () =>
    setCurrent((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  const next = () =>
    setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  // Auto-slide effect
  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, interval);
    return () => clearInterval(timer);
  }, [images.length, interval]);

  return (
    <div className="relative w-full h-56 sm:h-72 overflow-hidden rounded-xl shadow-lg">
      <img
        src={images[current]}
        alt={`Case study ${current + 1}`}
        className="w-full h-56 sm:h-72 object-cover rounded-xl transition-all duration-300 shadow"
      />
      {images.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-yellow-100 text-gray-700 rounded-full p-2 shadow transition"
            aria-label="Previous image"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-yellow-100 text-gray-700 rounded-full p-2 shadow transition"
            aria-label="Next image"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, idx) => (
              <span
                key={idx}
                className={`block w-2 h-2 rounded-full ${
                  current === idx ? "bg-yellow-400" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const CaseStudies = () => {
  return (
    <section
      className="w-full py-12 bg-#d0d44c-500 flex flex-col items-center"
      style={{ backgroundColor: "#d0d44c" }}
    >
      <h2 className="text-3xl font-bold mb-8 text-gray-800">Case Studies</h2>
      <div className="grid gap-8 md:grid-cols-2 w-full max-w-5xl px-4">
        {caseStudiesData.map((study, idx) => (
          <div
            key={idx}
            className="bg-white rounded-xl shadow-lg flex flex-col overflow-hidden hover:shadow-2xl transition group"
          >
            <ImageCarousel images={study.images} />
            <div className="p-6 flex-1 flex flex-col">
              <h3 className="text-xl font-semibold mb-2 text-gray-900 group-hover:text-gray-500 transition">
                {study.title}
              </h3>
              <p className="text-gray-600">{study.description}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default CaseStudies;
